import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {SortKey} from 'search/SearchConstants';
import Authors from 'ui-library/Authors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import fuzzydate from 'util/fuzzydate';
import {BasicEvent} from 'util/Types';
import {WorkflowDashboardDetails} from 'workflows/WorkflowConstants';

import {WorkflowSummaryGraphQL} from '../../../graphql-fragments/workflows/__generated/WorkflowSummaryGraphQL';
import WorkflowStatusSpan from './WorkflowStatusSpan';

export interface WorkflowListingExtras {
  workflowIdToDashboardDetails?: {[workflowId: string]: WorkflowDashboardDetails};
}
export type WorkflowColumnExtras = Pick<WorkflowListingExtras, 'workflowIdToDashboardDetails'>;

export default class WorkflowListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.WORKFLOW> {
  listingRendererType = ListingRendererType.WORKFLOW;

  renderIcon(): JSX.Element {
    return <BenchlingIcon className="scrollableTable-cell-icon" icon={ModelTypeConstants.WORKFLOW} />;
  }

  getNonPluginColumnConfigurations(
    workflows: Array<WorkflowSummaryGraphQL>,
    {workflowIdToDashboardDetails}: WorkflowListingExtras
  ): Array<ColumnConfiguration<WorkflowSummaryGraphQL>> {
    return _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: 'ID',
          width: 150,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
        },
        render: (workflow: WorkflowSummaryGraphQL) => workflow.humanId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (workflow: WorkflowSummaryGraphQL) => workflow.name,
      },
      {
        column: {accessorKey: ColumnAccessorKey.PROJECT, header: 'Project', flexGrow: 1},
        render: (workflow: WorkflowSummaryGraphQL) => workflow.source.name,
      },
      {
        column: {accessorKey: ColumnAccessorKey.AUTHORS, header: 'Authors', flexGrow: 1},
        render: (workflow: WorkflowSummaryGraphQL) => <Authors authors={workflow.authors} />,
      },
      workflowIdToDashboardDetails && {
        column: {accessorKey: ColumnAccessorKey.STATUS, header: 'Status', flexGrow: 1},
        render: (workflow: WorkflowSummaryGraphQL) =>
          workflowIdToDashboardDetails[workflow.id] && (
            <WorkflowStatusSpan workflowDashboardDetails={workflowIdToDashboardDetails[workflow.id]} />
          ),
      },
      workflowIdToDashboardDetails && {
        column: {
          accessorKey: ColumnAccessorKey.LAST_STATUS_CHANGE,
          header: 'Last status change',
          flexGrow: 1,
        },
        render: (workflow: WorkflowSummaryGraphQL) => {
          // Return the value to display in the Last Completed column.
          const workflowDashboardDetails = workflowIdToDashboardDetails[workflow.id];
          if (
            workflowDashboardDetails &&
            workflowDashboardDetails.lastStageCompletionChange &&
            workflowDashboardDetails.lastStageCompletionChange.lastStageCompletedAt
          ) {
            return fuzzydate(workflowDashboardDetails.lastStageCompletionChange.lastStageCompletedAt);
          }
          return null;
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: 'Created',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (workflow: WorkflowSummaryGraphQL) => fuzzydate(workflow.createdAt),
      },
    ]);
  }

  getNonPluginRowContentHeight(workflow: WorkflowSummaryGraphQL): number {
    return ScrollableTableConstants.TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(workflow: WorkflowSummaryGraphQL, e: BasicEvent, extras: unknown): void {
    // Not yet implemented
  }
}
